// src/components/Templates.js
import React, { useState, useEffect } from 'react';
import { templateApi } from '../services/api'; // Import from our service file

function Templates() {
  // State variables
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    category: ''
  });

  // Fetch templates when component mounts
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Function to fetch templates from API
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Using our templateApi service
      const response = await templateApi.getAll();
      console.log('Templates response:', response);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes in form
  const handleInputChange = (e) => {
    const { name: fieldName, value } = e.target;
    setFormData({
      ...formData,
      [fieldName]: value
    });
  };

  // Open modal in create mode
  const handleAddTemplate = () => {
    setEditMode(false);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      content: '',
      category: ''
    });
    setShowModal(true);
  };

  // Open modal in edit mode
  const handleEditTemplate = (template) => {
    setEditMode(true);
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject || '',
      content: template.content || '',
      category: template.category || ''
    });
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedTemplate) {
        // Update existing template
        await templateApi.update(selectedTemplate._id, formData);
      } else {
        // Create new template
        await templateApi.create(formData);
      }
      
      // Refresh template list
      fetchTemplates();
      
      // Close modal and reset form
      setShowModal(false);
      setFormData({
        name: '',
        subject: '',
        content: '',
        category: ''
      });
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  // Delete a template
  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await templateApi.delete(id);
        fetchTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  // Close the modal
  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Email Templates</h2>
        <button 
          className="btn btn-primary" 
          onClick={handleAddTemplate}
        >
          Add Template
        </button>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center my-5">
          <div className="mb-4">
            <img 
              src="/empty-state.svg" 
              alt="No templates" 
              style={{ maxWidth: '300px' }} 
            />
          </div>
          <h4>No templates found</h4>
          <p className="text-muted">Create your first email template to get started</p>
          <button 
            className="btn btn-primary" 
            onClick={handleAddTemplate}
          >
            Create Template
          </button>
        </div>
      ) : (
// Alternative layout for template cards

<div className="row">
  {templates.map((template) => (
    <div className="col-md-4 mb-4" key={template._id}>
      <div className="card h-100 shadow-sm">
        {/* Card with absolute positioned action buttons */}
        <div className="card-body position-relative">
          {/* Action buttons in top-right corner */}
          <div className="position-absolute top-0 end-0 p-2">
            <button 
              className="btn btn-sm btn-outline-primary me-1" 
              onClick={() => handleEditTemplate(template)}
              title="Edit template"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button 
              className="btn btn-sm btn-outline-danger" 
              onClick={() => handleDeleteTemplate(template._id)}
              title="Delete template"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
          
          {/* Category badge */}
          {template.category && (
            <span className="badge bg-secondary me-2 mb-2 d-inline-block">{template.category}</span>
          )}
          
          <h5 className="card-title mt-3">{template.name}</h5>
          {template.subject && (
            <h6 className="card-subtitle mb-2 text-muted">
              Subject: {template.subject}
            </h6>
          )}
          <div className="template-preview">
            <div 
              dangerouslySetInnerHTML={{ 
                __html: template.content && template.content.length > 150 
                  ? template.content.substring(0, 150) + '...' 
                  : template.content 
              }} 
            />
          </div>
        </div>
        <div className="card-footer">
          <button 
            className="btn btn-sm btn-outline-primary w-100" 
            onClick={() => handleEditTemplate(template)}
          >
            View Template
          </button>
        </div>
      </div>
    </div>
  ))}
</div>
      )}

      {/* Template Modal */}
      {showModal && (
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editMode ? 'Edit Template' : 'Create Template'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="templateName" className="form-label">Template Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="templateName"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="category" className="form-label">Category</label>
                      <input
                        type="text"
                        className="form-control"
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        placeholder="E.g. Follow-up, Welcome, etc."
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="subject" className="form-label">Subject Line</label>
                    <input
                      type="text"
                      className="form-control"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Email subject line"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">Email Content</label>
                    <textarea
                      className="form-control"
                      id="content"
                      name="content"
                      rows="8"
                      value={formData.content}
                      onChange={handleInputChange}
                      placeholder="Enter email content (HTML supported)"
                      required
                    ></textarea>
                    <small className="form-text text-muted">
                      You can use variables like {'{{'} {'contactName'} {'}},'} {'{{'} {'companyName'} {'}},'} etc. in your template.
                    </small>
                  </div>
                  
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save Template
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add modal backdrop */}
      {showModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}

export default Templates;